import {
  isAwaitingAnswerLike,
  isAwaitingAskLike,
  type AgentEvent,
  type AppState,
  type Chat,
} from '@/app/state/types';
import { resolveChatSummaryActiveRun } from '@/features/chats/lib/chatRunState';
import {
  readEventChatName,
  readEventFirstAgentName,
  readEventTeamId,
} from '@/shared/utils/eventFieldReaders';
import { toText } from '@/shared/utils/eventUtils';
import { isEpochMillis } from '@/shared/utils/platformTime';

export interface LiveChatSummaryCache {
  chatId: string;
  runId: string;
  agentKey: string;
  teamId: string;
}

export interface LiveChatSummaryContext {
  agentKey: string;
  teamId: string;
}

export function resolveChatSummaryUpdatedAt(
  event: AgentEvent,
): number | undefined {
  const raw = event as Record<string, unknown>;
  if (isEpochMillis(raw.updatedAt)) {
    return raw.updatedAt;
  }
  if (isEpochMillis(raw.createdAt)) {
    return raw.createdAt;
  }
  if (isEpochMillis(event.timestamp)) {
    return event.timestamp;
  }
  return undefined;
}

export function resolveChatSummaryPendingAwaiting(
  event: AgentEvent,
): boolean | undefined {
  const type = toText(event.type);
  if (isAwaitingAskLike(type)) {
    return true;
  }
  if (
    isAwaitingAnswerLike(type)
    || type === 'request.query'
    || type === 'run.start'
    || type === 'run.complete'
    || type === 'run.error'
    || type === 'run.cancel'
  ) {
    return false;
  }
  return undefined;
}

export function upsertLiveChatSummary(input: {
  event: AgentEvent;
  cache: LiveChatSummaryCache;
  state: Pick<AppState, 'chatId' | 'runId' | 'chats' | 'chatAgentById'>;
  selectedContext: LiveChatSummaryContext;
  lastRunContent?: string;
}): {
  chat: Partial<Chat> & Pick<Chat, 'chatId'>;
  resolved: LiveChatSummaryCache;
} | null {
  const { event, cache, state, selectedContext, lastRunContent } = input;
  const chatId = toText(event.chatId) || cache.chatId || toText(state.chatId);
  if (!chatId) {
    return null;
  }

  const runId = toText(event.runId) || cache.runId || toText(state.runId);
  const existingChat = state.chats.find((chat) => toText(chat?.chatId) === chatId);
  const rememberedAgentKey = toText(state.chatAgentById.get(chatId));
  const agentKey =
    toText(event.agentKey) ||
    cache.agentKey ||
    rememberedAgentKey ||
    toText(existingChat?.agentKey || existingChat?.firstAgentKey) ||
    selectedContext.agentKey;
  const teamId =
    readEventTeamId(event) ||
    cache.teamId ||
    toText(existingChat?.teamId) ||
    selectedContext.teamId;
  const source = toText(event.source) || toText(existingChat?.source);
  const updatedAt = resolveChatSummaryUpdatedAt(event);
  const hasPendingAwaiting = resolveChatSummaryPendingAwaiting(event);
  const hasActiveRun = resolveChatSummaryActiveRun(event);

  return {
    chat: {
      chatId,
      chatName: readEventChatName(event) || toText(existingChat?.chatName) || undefined,
      firstAgentName:
        readEventFirstAgentName(event) ||
        toText(existingChat?.firstAgentName) ||
        undefined,
      firstAgentKey: agentKey || undefined,
      agentKey: agentKey || undefined,
      teamId: teamId || undefined,
      source: source || undefined,
      lastRunId: runId || undefined,
      lastRunContent,
      updatedAt,
      hasPendingAwaiting,
      hasActiveRun,
      activeRun: hasActiveRun === true
        ? {
            runId,
            ...(agentKey ? { agentKey } : {}),
          }
        : hasActiveRun === false
          ? null
          : undefined,
    },
    resolved: {
      chatId,
      runId,
      agentKey,
      teamId,
    },
  };
}
